import type { Rng } from '../rng.js'
import type { TeddyKey } from '../teddies.js'

/**
 * What an answer means, and nothing else.
 *
 * This file is the single definition of truth in Chaka's Mastermind, and it is
 * here for the same reason `riddle-solve.ts` is: the thing the game says back
 * to her is the whole game. She cannot see the hidden row. Every deduction she
 * makes rests on these two numbers being right, so a wrong answer does not
 * look like a bug — it looks like her reasoning failing. She follows it
 * correctly to a contradiction and is told she lost.
 *
 * **The bug this file exists to refuse** is the one every implementation of
 * this game gets wrong at least once: counting "right teddy, wrong place" by
 * walking the row and striking teddies off as they match. That is the obvious
 * way to write it, it is correct whenever no teddy is repeated, and it is
 * silently wrong the moment one is — which is exactly the size this game grows
 * into.
 *
 * So the count is not a walk. It is the size of the overlap between two bags
 * of teddies, `Σ min(how many the secret has, how many the guess has)`, which
 * has no order in it to get wrong. `exact` is subtracted from that total
 * rather than counted separately, because a teddy in the right place is also a
 * teddy in common and must not be counted twice.
 */

/** A peg. The teddies are the palette, so a guess is a row of them. */
export type Peg = TeddyKey

export interface Score {
  /** Right teddy, right place. */
  readonly exact: number
  /** Right teddy, wrong place. Never counts one that is already `exact`. */
  readonly colour: number
}

/**
 * How a guess is answered.
 *
 * Both rows must be the same length; a caller handing in a part-built row is a
 * caller that has not checked whether the row is full, which is a bug rather
 * than a thing to be lenient about.
 *
 * Worth knowing when reading the tests: this is **symmetric**. Swapping the
 * secret and the guess gives the same answer, because both halves of it are
 * symmetric — positions that agree, and the overlap of two bags. That is not
 * an obvious property, which is what makes it a good test: the strike-off bug
 * above is *not* symmetric, so it cannot hide from it.
 */
export function scoreGuess(secret: readonly Peg[], guess: readonly Peg[]): Score {
  if (secret.length !== guess.length) {
    throw new Error(`A ${guess.length}-peg guess against a ${secret.length}-peg row`)
  }

  let exact = 0
  for (const [at, peg] of guess.entries()) {
    if (peg === secret[at]) exact += 1
  }

  const inSecret = tally(secret)
  const inGuess = tally(guess)

  let shared = 0
  for (const [peg, count] of inSecret) {
    shared += Math.min(count, inGuess.get(peg) ?? 0)
  }

  return { exact, colour: shared - exact }
}

/** How many of each teddy a row holds. */
function tally(row: readonly Peg[]): Map<Peg, number> {
  const counts = new Map<Peg, number>()
  for (const peg of row) counts.set(peg, (counts.get(peg) ?? 0) + 1)

  return counts
}

/** Whether the row is the hidden one — the only thing that ends a round won. */
export function isFound(score: Score, pegs: number): boolean {
  return score.exact === pegs
}

/**
 * How hard a round is, as the three things that actually change.
 *
 * `repeats` is the one that matters most and it is deliberately last to
 * arrive. Without it she can rule a teddy out of every other place the moment
 * she has settled one, which is the whole of the reasoning at eight. With it
 * nothing can ever be fully ruled out, and that is a different game.
 */
export interface MastermindShape {
  readonly pegs: number
  /** How many teddies she picks from. */
  readonly palette: number
  readonly repeats: boolean
}

export const MASTERMIND_SIZES = ['gentle', 'middling', 'proper'] as const
export type MastermindSize = (typeof MASTERMIND_SIZES)[number]

export function isMastermindSize(value: unknown): value is MastermindSize {
  return MASTERMIND_SIZES.includes(value as MastermindSize)
}

export const MASTERMIND_SHAPES: Readonly<Record<MastermindSize, MastermindShape>> = {
  gentle: { pegs: 4, palette: 6, repeats: false },
  middling: { pegs: 5, palette: 6, repeats: false },
  proper: { pegs: 5, palette: 6, repeats: true },
}

export const DEFAULT_MASTERMIND_SIZE: MastermindSize = 'gentle'

/** How many goes she gets, at every size. The ticket's number, and it is generous. */
export const GOES_ALLOWED = 10

/**
 * The teddies she picks from.
 *
 * Six, chosen by looking at the photographs rather than by picking names off a
 * list. They have to stay tellable apart at peg size, across a room, and for
 * somebody who cannot distinguish some colours — and **different species does
 * most of that work where different colours does not**. An octopus, a fish and
 * an owl are not one another at any size or in any light; two squishmallows
 * are two round blobs.
 *
 * What was tried and rejected, so nobody re-adds them:
 *
 * - **Snowy**, a grey-and-white husky, next to **Owlie**, a grey-and-cream
 *   owl. Two grey round things, and at the size ten rows of history leaves a
 *   peg they stop being two.
 * - **Sammy**, a bright blue shark, next to **Sky**, a teal octopus. The
 *   silhouettes differ but the hue is the first thing read from a sofa, and
 *   these read as the same one.
 *
 * Red Tommy and orange Nemo are the closest surviving pair in hue, and they
 * are kept because nothing else about them is alike: a round tomato with legs
 * and a striped fish are not confusable by shape at any size.
 *
 * Chaka is not among them. He is the one asking the question, and a host who
 * is also a peg reads as a mistake rather than as a joke.
 *
 * Exactly as many as the largest palette any size asks for. A seventh sitting
 * here unused would be config that decides nothing, which this repo has been
 * caught shipping before.
 */
export const MASTERMIND_PALETTE: readonly Peg[] = [
  'sky',
  'nemo',
  'owlie',
  'tigger',
  'tommy',
  'rudolf',
]

/** The teddies a round of this size picks from. */
export function paletteFor(size: MastermindSize): readonly Peg[] {
  return MASTERMIND_PALETTE.slice(0, MASTERMIND_SHAPES[size].palette)
}

/**
 * The hidden row.
 *
 * Drawn from the seed the server issued, so the same round dealt twice is the
 * same round — which is what lets a play be re-derived and recorded rather
 * than taken on the browser's word.
 */
export function secretFor(rng: Rng, size: MastermindSize): readonly Peg[] {
  const { pegs, repeats } = MASTERMIND_SHAPES[size]
  const palette = paletteFor(size)

  if (repeats) {
    return Array.from({ length: pegs }, () => rng.pick(palette))
  }

  /*
   * Without repeats, drawn by taking from a dwindling pool rather than by
   * drawing and retrying. Retrying would work and would have no bound on how
   * long it took, which is the sort of thing that is fine until the palette
   * and the peg count are close together.
   */
  const pool = [...palette]
  const row: Peg[] = []
  for (let at = 0; at < pegs; at++) {
    const taken = rng.pick(pool)
    row.push(taken)
    pool.splice(pool.indexOf(taken), 1)
  }

  return row
}
